import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { T, mono } from "./utils";

export const NAV_ITEMS = [
  { id:"home",      label:"Home"             },
  { id:"ortak",     label:"Co-Productions"   },
  { id:"ana",       label:"Main Productions" },
  { id:"etc",       label:"ETC."             },
  { id:"finans",    label:"Finance"          },
  { id:"sosyal",    label:"Social"           },
  { id:"takvim",    label:"Calendar"         },
  { id:"festivals", label:"Festivals"        },
  { id:"summary",   label:"Summary"          },
  { id:"shooting",  label:"Shooting"         },
  { id:"avans",     label:"Avans"            },
  { id:"contacts",  label:"Kişiler"          },
  { id:"info",      label:"Info"             },
];

// "Fol" (password "fol") is the hardcoded master user — full access, always
// has every tab + Settings, can't be edited or deleted here.
export const DEFAULT_USERS = [
  { id:"team",     label:"Fol Team",          password:"folteam", nav: NAV_ITEMS.filter(n=>n.id!=="finans").map(n=>n.id) },
  { id:"social",   label:"Sezer",             password:"sezer",   nav: ["sosyal"] },
  { id:"festival", label:"Dilşad (Festival)", password:"dilşad",  nav: ["festivals","shooting"] },
  { id:"ada",      label:"Ada",               password:"ada",     nav: NAV_ITEMS.filter(n=>!["finans","sosyal","shooting","etc","summary"].includes(n.id)).map(n=>n.id) },
  { id:"dilsad",   label:"Dilsad",            password:"dilsad",  nav: NAV_ITEMS.filter(n=>n.id!=="finans").map(n=>n.id) },
];

const TR_MAP = { ç:"c", ğ:"g", ı:"i", ö:"o", ş:"s", ü:"u", İ:"i" };
function slugify(label, existingIds) {
  let base = (label||"").toLowerCase().split("").map(c=>TR_MAP[c]||c).join("").replace(/[^a-z0-9]+/g,"") || "kullanici";
  let id = base, n = 2;
  while (existingIds.includes(id)) { id = base + n; n++; }
  return id;
}

export function SettingsView() {
  const [users, setUsers] = useState(DEFAULT_USERS);
  const [projects, setProjects] = useState([]);
  const [hiddenProjects, setHiddenProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [posterUploading, setPosterUploading] = useState(null); // project id

  useEffect(()=>{ loadAll(); },[]); // eslint-disable-line

  async function loadAll(){
    setLoading(true);
    const { data: usersRow } = await supabase.from("app_settings").select("value").eq("key","users").maybeSingle();
    setUsers(usersRow?.value && usersRow.value.length ? usersRow.value : DEFAULT_USERS);
    const { data: hiddenRow } = await supabase.from("app_settings").select("value").eq("key","hidden_task_projects").maybeSingle();
    setHiddenProjects(hiddenRow?.value?.ids || []);
    const { data: projs } = await supabase.from("projects").select("id,title,kind,phase,poster_url").order("title");
    setProjects((projs||[]).filter(p=>p.phase!=="Değerlendirilecek"));
    setLoading(false);
  }

  function flashSaved(){ setSaved(true); setTimeout(()=>setSaved(false),1200); }

  async function persistUsers(next){
    setUsers(next);
    await supabase.from("app_settings").upsert({ key:"users", value:next }, { onConflict:"key" });
    flashSaved();
  }

  function toggleNav(userId, navId){
    persistUsers(users.map(u=>u.id===userId
      ? { ...u, nav: u.nav.includes(navId) ? u.nav.filter(x=>x!==navId) : [...u.nav, navId] }
      : u));
  }

  // Live-edit label/password locally, commit to DB on blur
  function editField(userId, field, value){
    setUsers(prev=>prev.map(u=>u.id===userId?{...u,[field]:value}:u));
  }
  function commitField(userId, field, value){
    if (field==="password") {
      const trimmed = (value||"").trim();
      if (trimmed && (trimmed==="fol" || users.some(u=>u.id!==userId && u.password===trimmed))) {
        alert("Bu şifre başka bir kullanıcı tarafından kullanılıyor (veya 'fol' rezerve).");
        loadAll();
        return;
      }
    }
    persistUsers(users.map(u=>u.id===userId?{...u,[field]:value}:u));
  }

  function addUser(){
    const label = "Yeni Kullanıcı";
    const id = slugify(label+Date.now(), users.map(u=>u.id));
    persistUsers([...users, { id, label, password:"", nav:["home"] }]);
  }

  function removeUser(userId){
    if(!window.confirm("Bu kullanıcıyı silmek istediğine emin misin?")) return;
    persistUsers(users.filter(u=>u.id!==userId));
  }

  async function toggleProject(projId){
    const next = hiddenProjects.includes(projId) ? hiddenProjects.filter(x=>x!==projId) : [...hiddenProjects, projId];
    setHiddenProjects(next);
    await supabase.from("app_settings").upsert({ key:"hidden_task_projects", value:{ ids:next } }, { onConflict:"key" });
    flashSaved();
  }

  async function uploadPoster(projId, file) {
    if (!file) return;
    setPosterUploading(projId);
    const ext = file.name.split(".").pop();
    const path = `${projId}.${ext}`;
    const { error } = await supabase.storage.from("posters").upload(path, file, { upsert: true });
    if (error) { alert("Yükleme hatası: " + error.message); setPosterUploading(null); return; }
    const { data: { publicUrl } } = supabase.storage.from("posters").getPublicUrl(path);
    await supabase.from("projects").update({ poster_url: publicUrl }).eq("id", projId);
    setProjects(prev => prev.map(p => p.id === projId ? { ...p, poster_url: publicUrl } : p));
    setPosterUploading(null);
    flashSaved();
  }

  async function removePoster(projId) {
    await supabase.from("projects").update({ poster_url: null }).eq("id", projId);
    setProjects(prev => prev.map(p => p.id === projId ? { ...p, poster_url: null } : p));
    flashSaved();
  }

  if (loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  const cellInput = { width:"100%",border:"none",background:"none",fontSize:12,fontFamily:"'Inter',sans-serif",color:T.ink,padding:"2px 4px",outline:"none",borderBottom:`1px dashed ${T.border}` };

  return (
    <div style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>
      <div style={{maxWidth:1000,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:28}}>
          <div>
            <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4,letterSpacing:"0.15em"}}>SETTINGS</div>
            <div style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em"}}>Erişim ve Görünürlük</div>
          </div>
          {saved&&<span style={{...mono,fontSize:9,color:"#2d7a2d"}}>kaydedildi ✓</span>}
        </div>

        {/* Users + nav access */}
        <div style={{marginBottom:36}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
            <div style={{fontSize:14,fontWeight:600}}>Kullanıcılar ve Sekme Erişimleri</div>
            <button onClick={addUser} style={{...mono,fontSize:9,background:T.ink,color:"#fff",border:"none",borderRadius:2,padding:"5px 10px",cursor:"pointer"}}>+ Kullanıcı Ekle</button>
          </div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:12}}>Şifre ile giren her kullanıcının görebileceği menü sekmeleri</div>
          <div style={{border:`1px solid ${T.border}`,borderRadius:2,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr>
                  <th style={{...mono,fontSize:9,color:T.mute,textAlign:"left",padding:"8px 10px",borderBottom:`1px solid ${T.border}`,minWidth:120}}>Kullanıcı</th>
                  <th style={{...mono,fontSize:9,color:T.mute,textAlign:"left",padding:"8px 10px",borderBottom:`1px solid ${T.border}`,minWidth:90}}>Şifre</th>
                  {NAV_ITEMS.map(n=>(
                    <th key={n.id} style={{...mono,fontSize:9,color:T.mute,textAlign:"center",padding:"8px 6px",borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap"}}>{n.label}</th>
                  ))}
                  <th style={{borderBottom:`1px solid ${T.border}`}}></th>
                </tr>
              </thead>
              <tbody>
                {/* Fol — fixed master user */}
                <tr style={{background:"#fff"}}>
                  <td style={{padding:"8px 10px",borderBottom:`1px solid ${T.borderLight}`,fontWeight:600}}>Fol</td>
                  <td style={{padding:"8px 10px",borderBottom:`1px solid ${T.borderLight}`,...mono,fontSize:10,color:T.mute}}>fol</td>
                  {NAV_ITEMS.map(n=>(
                    <td key={n.id} style={{textAlign:"center",padding:"8px 6px",borderBottom:`1px solid ${T.borderLight}`}}>
                      <input type="checkbox" checked disabled style={{cursor:"not-allowed",opacity:0.5}}/>
                    </td>
                  ))}
                  <td style={{borderBottom:`1px solid ${T.borderLight}`,padding:"8px 6px",...mono,fontSize:8,color:T.border,textAlign:"center"}}>sabit</td>
                </tr>
                {users.map((u,i)=>(
                  <tr key={u.id} style={{background:i%2===0?"#fff":T.bg}}>
                    <td style={{padding:"6px 10px",borderBottom:i<users.length-1?`1px solid ${T.borderLight}`:"none"}}>
                      <input value={u.label} onChange={e=>editField(u.id,"label",e.target.value)} onBlur={e=>commitField(u.id,"label",e.target.value)} style={cellInput}/>
                    </td>
                    <td style={{padding:"6px 10px",borderBottom:i<users.length-1?`1px solid ${T.borderLight}`:"none"}}>
                      <input value={u.password} onChange={e=>editField(u.id,"password",e.target.value)} onBlur={e=>commitField(u.id,"password",e.target.value)} placeholder="şifre…" style={{...cellInput,...mono,fontSize:10}}/>
                    </td>
                    {NAV_ITEMS.map(n=>(
                      <td key={n.id} style={{textAlign:"center",padding:"6px 6px",borderBottom:i<users.length-1?`1px solid ${T.borderLight}`:"none"}}>
                        <input type="checkbox" checked={u.nav.includes(n.id)} onChange={()=>toggleNav(u.id,n.id)} style={{cursor:"pointer"}}/>
                      </td>
                    ))}
                    <td style={{textAlign:"center",padding:"6px 6px",borderBottom:i<users.length-1?`1px solid ${T.borderLight}`:"none"}}>
                      <button onClick={()=>removeUser(u.id)} title="Kullanıcıyı sil" style={{background:"none",border:"none",color:"#c0392b",fontSize:14,cursor:"pointer",padding:0}}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{...mono,fontSize:9,color:T.mute,marginTop:8}}>Değişiklikler, ilgili kullanıcının bir sonraki girişinde veya sayfa yenilemesinde uygulanır. "Settings" sekmesi sadece Fol erişimine her zaman görünür.</div>
        </div>

        {/* Poster upload */}
        <div style={{marginBottom:36}}>
          <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Film Afişleri</div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:12}}>Festival landing ekranında görünecek afiş görselleri</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12}}>
            {projects.map(p=>(
              <div key={p.id} style={{border:`1px solid ${T.border}`,borderRadius:4,overflow:"hidden",background:"#fff"}}>
                {/* Poster image */}
                <div style={{height:200,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
                  {p.poster_url
                    ? <img src={p.poster_url} alt={p.title} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : <span style={{...mono,fontSize:9,color:T.border}}>Afiş yok</span>
                  }
                  {posterUploading===p.id && (
                    <div style={{position:"absolute",inset:0,background:"rgba(255,255,255,0.8)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{...mono,fontSize:9,color:T.mute}}>Yükleniyor…</span>
                    </div>
                  )}
                </div>
                {/* Title + actions */}
                <div style={{padding:"8px 10px"}}>
                  <div style={{fontSize:11,fontWeight:500,color:T.ink,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.title}</div>
                  <div style={{display:"flex",gap:4}}>
                    <label style={{flex:1,cursor:"pointer"}}>
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>uploadPoster(p.id,e.target.files[0])}/>
                      <span style={{...mono,fontSize:8,display:"block",textAlign:"center",padding:"3px 0",border:`1px solid ${T.border}`,borderRadius:2,color:T.mute,cursor:"pointer"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.ink}
                        onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                        {p.poster_url?"Değiştir":"+ Yükle"}
                      </span>
                    </label>
                    {p.poster_url&&(
                      <button onClick={()=>removePoster(p.id)} style={{...mono,fontSize:8,background:"none",border:`1px solid #fde8e8`,color:"#c0392b",borderRadius:2,padding:"3px 6px",cursor:"pointer"}}>Sil</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Task bar visibility */}
        <div>
          <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Görev Çubuğu Görünürlüğü</div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:12}}>Home sayfasındaki görev ekleme menüsünde görünecek proje başlıkları</div>
          <div style={{border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
            {projects.map((p,i)=>(
              <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:i%2===0?"#fff":T.bg,borderBottom:i<projects.length-1?`1px solid ${T.borderLight}`:"none",cursor:"pointer",fontSize:12}}>
                <input type="checkbox" checked={!hiddenProjects.includes(p.id)} onChange={()=>toggleProject(p.id)} style={{cursor:"pointer"}}/>
                <span>{p.title}</span>
              </label>
            ))}
            {projects.length===0&&<div style={{...mono,fontSize:9,color:T.mute,padding:"10px 12px"}}>Proje yok.</div>}
          </div>
          <div style={{...mono,fontSize:9,color:T.mute,marginTop:8}}>"ETC." sekmesi her zaman görünür.</div>
        </div>
      </div>
    </div>
  );
}
